import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { Journey } from '../../core/navigation/journey';
import { VisitService } from '../../core/visit/visit.service';
import { FooterComponent } from '../../layout/footer/footer.component';
import { HeaderComponent } from '../../layout/header/header.component';
import { FooterNavComponent } from '../../shared/components/footer-nav/footer-nav.component';
import { IdentificationCardComponent } from '../../shared/components/identification-card/identification-card.component';

interface ModeCopy {
  title: string;
  qrSubtitle: string;
  manualSubtitle: string;
  qrRoute: string;
  manualRoute: string;
}

/**
 * Écran intermédiaire « Avec / Sans QR code », partagé par l'arrivée et le départ.
 *
 * Un seul composant paramétré plutôt que deux jumeaux : les deux parcours posent
 * littéralement la même question, avec les mêmes cartes, la même navigation
 * retour et le même clavier en aval. Dupliquer garantirait une divergence au
 * premier correctif.
 *
 * Le parcours vient du **paramètre de route** et non d'un service : la borne est
 * en `withHashLocation()`, un rechargement ne doit pas perdre le contexte.
 */
@Component({
  selector: 'app-identification-mode',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, FooterNavComponent, IdentificationCardComponent],
  templateUrl: './identification-mode.component.html',
  styleUrl: './identification-mode.component.scss',
})
export class IdentificationModeComponent {
  private readonly router = inject(Router);
  private readonly visitService = inject(VisitService);

  /** Lié depuis le paramètre de route (`withComponentInputBinding`). */
  readonly journey = input<string>('checkin');

  private static readonly COPY: Record<Journey, ModeCopy> = {
    checkin: {
      title: 'Signaler mon arrivée',
      qrSubtitle: 'Scanner mon invitation',
      manualSubtitle: "S'enregistrer manuellement",
      qrRoute: '/scan-qr',
      manualRoute: '/visitor-identification',
    },
    checkout: {
      title: 'Signaler mon départ',
      qrSubtitle: 'Scanner mon QR code de visite',
      manualSubtitle: 'Rechercher ma visite',
      qrRoute: '/checkout/scan-qr',
      manualRoute: '/checkout/search',
    },
  };

  readonly currentJourney = computed<Journey>(() =>
    this.journey() === 'checkout' ? 'checkout' : 'checkin',
  );

  readonly copy = computed<ModeCopy>(
    () => IdentificationModeComponent.COPY[this.currentJourney()],
  );

  onQrPressed(): void {
    if (this.currentJourney() === 'checkin') {
      // Le mode d'entrée n'est connu qu'ici : l'accueil ne le décide plus.
      this.visitService.ensureVisit();
      // Un refus (avance, retard, annulé) laisse la session ouverte : sans remise
      // à zéro, le passage suivant reprendrait son compteur et son 1er échec
      // tomberait directement sur l'identification manuelle, sans rescan.
      this.visitService.updateFlow({ entryMethod: 'qr', qrScanAttempts: 0 });
      this.visitService.trackEvent('qr_scan_attempt');
    }
    void this.router.navigate([this.copy().qrRoute]);
  }

  onManualPressed(): void {
    if (this.currentJourney() === 'checkin') {
      this.visitService.ensureVisit();
      this.visitService.updateFlow({ entryMethod: 'manual' });
      this.visitService.trackEvent('manual_identification');
    }
    // Parcours départ : aucune écriture sur VisitService. Le check-out n'est pas
    // une session borne, il met à jour la visite d'arrivée (SPECS §6).
    void this.router.navigate([this.copy().manualRoute]);
  }
}
